/**
 * Pipeline Visualization Types
 * Defines interfaces for the 14 AI agents and their statuses
 */

export type AgentStatusType = 'idle' | 'running' | 'complete' | 'error' | 'waiting_human' | 'queued';

export interface AgentProgress {
  current: number;
  total: number;
  percentage: number;
}

export interface AgentResult {
  summary: string;
  details?: Record<string, any>;
  itemsNeedingReview?: number;
}

export interface AgentStatus {
  agentId: string;
  agentName: string;
  persona: string;
  icon: string;
  status: AgentStatusType;
  progress?: AgentProgress;
  result?: AgentResult;
  startedAt?: string;
  completedAt?: string;
  nextAgent?: string;
  tier?: 'tier1' | 'tier2' | 'tier3';  // For classifier breakdown
}

export interface ClaimPipeline {
  claimId: string;
  claimName: string;
  agents: AgentStatus[];
  currentAgent?: string;
  pendingHumanReview: number;
  estimatedCompletion?: string;
  lastUpdated: string;
}

// The 14 AI Agents with their roles
export const AI_AGENTS: Record<string, Omit<AgentStatus, 'status'>> = {
  document_parser: {
    agentId: 'document_parser',
    agentName: 'Document Parser AI',
    persona: 'Financial file analyst (Excel, CSV, IIF)',
    icon: '📄',
    nextAgent: 'transaction_classifier'
  },
  transaction_classifier: {
    agentId: 'transaction_classifier',
    agentName: 'Transaction Classifier AI',
    persona: 'Financial transaction classifier (3-tier)',
    icon: '🔍',
    nextAgent: 'anomaly_detective'
  },
  anomaly_detective: {
    agentId: 'anomaly_detective',
    agentName: 'Anomaly Detective AI',
    persona: 'Forensic AI, anomaly detection specialist',
    icon: '🕵️',
    nextAgent: 'fraud_detective'
  },
  fraud_detective: {
    agentId: 'fraud_detective',
    agentName: 'Fraud Detective AI',
    persona: "Forensic accountant (Benford's Law)",
    icon: '🔎',
    nextAgent: 'forensic_accountant'
  },
  tax_expert: {
    agentId: 'tax_expert',
    agentName: 'Tax Expert AI',
    persona: 'Expert tax accountant (1065, K-1)',
    icon: '📋',
  },
  bank_pl_analyst: {
    agentId: 'bank_pl_analyst',
    agentName: 'Bank/P&L Analyst AI',
    persona: 'Expert financial analyst',
    icon: '🏦',
  },
  data_specialist: {
    agentId: 'data_specialist',
    agentName: 'Data Specialist AI',
    persona: 'Forensic accountant colleague, 15+ years',
    icon: '📊',
  },
  forensic_accountant: {
    agentId: 'forensic_accountant',
    agentName: 'Forensic Accountant AI',
    persona: 'Senior forensic accountant, 20+ years BI claims',
    icon: '📈',
    nextAgent: 'attorney'
  },
  policy_analyst: {
    agentId: 'policy_analyst',
    agentName: 'Policy Analyst AI',
    persona: 'Insurance policy specialist',
    icon: '📜',
  },
  attorney: {
    agentId: 'attorney',
    agentName: 'Attorney AI',
    persona: 'Plaintiff attorney, 20+ years maximizing recovery',
    icon: '⚖️',
    nextAgent: 'adjuster'
  },
  adjuster: {
    agentId: 'adjuster',
    agentName: 'Adjuster AI',
    persona: 'CPA/CFF/CFE, 25+ years at J.S. Held/FTI',
    icon: '🔬',
    nextAgent: 'mediator'
  },
  mediator: {
    agentId: 'mediator',
    agentName: 'Mediator AI',
    persona: 'Monte Carlo settlement specialist',
    icon: '💰',
    nextAgent: 'report_writer'
  },
  calculation_explainer: {
    agentId: 'calculation_explainer',
    agentName: 'Calculation Explainer AI',
    persona: 'Technical explanation specialist',
    icon: '🧮',
  },
  report_writer: {
    agentId: 'report_writer',
    agentName: 'Report Writer AI',
    persona: 'Litigation-ready report specialist',
    icon: '📝',
  }
};

// Standard pipeline stages (main flow)
export const PIPELINE_STAGES = [
  'document_parser',
  'transaction_classifier', 
  'anomaly_detective',
  'forensic_accountant',
  'attorney',
  'mediator',
  'report_writer'
];
